import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./DropGame.css";
import "./MunchGame.css";
import "./CollieGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { AnswerResult } from "../Quiz/answer.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { BorderCollie, CollieGame } from "./CollieGame.tsx";
import { CollieState } from "./collieGame.ts";
import { flashCardSettings } from "../deck.ts";
import { collieGameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

interface ColliePageProps {
    slug: string;
    operation: Operations;
}

const pickIndex = (n: number) => getRandomInt(0, n - 1);

/**
 * Collie Crossing (ADR 0057) in its keyword landing page. A quiz route
 * carrying `game: "collie"`; its `quiz` is only the operation the picker opens
 * on (ADR 0098 §4). The game starts on a press, not on load, so the page does
 * not grab keyboard focus from a visitor who is only reading.
 */
export function ColliePage({ slug, operation }: ColliePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    // Only the picker's buttons use these: the floors enumerate fact families
    // rather than drawing from the generator, so there are no digit ranges.
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    // Captured at the start, so a crossing is reported for what was actually played.
    const [playedOperations, setPlayedOperations] = useState<string[]>([]);
    const [started, setStarted] = useState(false);
    const [missesVersion, setMissesVersion] = useState(0);

    const handleFact = useCallback((front: string, result: AnswerResult) => {
        if (result === "correct") {
            recordCorrect(factKey(front));
        } else {
            recordMiss(factKey(front));
        }
        setMissesVersion((v) => v + 1);
    }, []);

    const handleCrossed = useCallback(
        (state: CollieState) => {
            track("quiz_completed", collieGameEventParams(playedOperations, state));
        },
        [playedOperations]
    );

    function start() {
        setPlayedOperations(operationsOf(settings));
        setStarted(true);
    }

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool collieTool">
                {started ? (
                    <CollieGame pick={pickIndex} operations={playedOperations} onFact={handleFact} onCrossed={handleCrossed} />
                ) : (
                    <div className="collieIntro">
                        <BorderCollie className="collieIntroDog" />
                        <p>{t("flashCards.collieHowTo")}</p>
                        <p className="quizModePrompt">{t("flashCards.collieWhichFacts")}</p>
                        <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.collieWhichFacts")} />
                        <button type="button" className="dropStart" onClick={start}>
                            {t("flashCards.collieStart")}
                        </button>
                    </div>
                )}
                <MissedFactsPractice refreshKey={missesVersion} />
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default ColliePage;
